import { loadLottoReleaseJson, loadLottoWebDataRelease } from "./lotto-web-data";

export interface LottoStore {
  readonly id: string;
  readonly name: string;
  readonly address: string | null;
  readonly region: string;
  readonly latitude: number;
  readonly longitude: number;
  readonly firstPrizeCount: number;
  readonly secondPrizeCount: number;
}

/** A browser-only map viewport. It is never sent to AppForge. */
export interface LottoMapViewport {
  readonly south: number;
  readonly west: number;
  readonly north: number;
  readonly east: number;
  readonly zoom: number;
}

export async function loadPublishedStores(): Promise<readonly LottoStore[]> {
  const release = await loadLottoWebDataRelease();
  if (release === null) return loadLegacyStores();
  const value = await loadLottoReleaseJson<{ stores?: unknown }>(release, release.storesPath);
  return validateStores(value, release.dataVersion);
}

async function loadLegacyStores(): Promise<readonly LottoStore[]> {
  return loadStores("/data/stores.json");
}

async function loadStores(url: string): Promise<readonly LottoStore[]> {
  const response = await fetch(url, { headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error("store_data_unavailable");
  const value = await response.json() as { stores?: unknown };
  return validateStores(value);
}

function validateStores(value: { stores?: unknown; schemaVersion?: unknown; dataVersion?: unknown }, expectedDataVersion?: number): readonly LottoStore[] {
  if (expectedDataVersion !== undefined && (value.schemaVersion !== 1 || value.dataVersion !== expectedDataVersion)) {
    throw new Error("store_data_release_mismatch");
  }
  if (!Array.isArray(value.stores)) throw new Error("invalid_store_data");
  return value.stores.filter((store): store is LottoStore => typeof store === "object" && store !== null &&
    typeof (store as LottoStore).id === "string" && typeof (store as LottoStore).name === "string" &&
    Number.isFinite((store as LottoStore).latitude) && Number.isFinite((store as LottoStore).longitude) &&
    Number.isInteger((store as LottoStore).firstPrizeCount) && Number((store as LottoStore).firstPrizeCount) >= 0 &&
    Number.isInteger((store as LottoStore).secondPrizeCount) && Number((store as LottoStore).secondPrizeCount) >= 0);
}

export function searchStores(stores: readonly LottoStore[], query: string): readonly LottoStore[] {
  const normalized = normalizeSearch(query);
  if (!normalized) return stores;
  return stores.filter((store) => normalizeSearch(`${store.name}${store.address ?? ""}${store.region}`).includes(normalized));
}

export function storesInViewport(
  stores: readonly LottoStore[],
  viewport: LottoMapViewport | null,
  selectedStoreId: string | null,
): readonly LottoStore[] {
  const candidates = viewport === null ? stores : stores.filter((store) => pointIsInViewport(store, viewport));
  const ordered = [...candidates].sort((left, right) => {
    const rightScore = right.firstPrizeCount * 10_000 + right.secondPrizeCount;
    const leftScore = left.firstPrizeCount * 10_000 + left.secondPrizeCount;
    return rightScore - leftScore || left.name.localeCompare(right.name, "ko-KR") || left.id.localeCompare(right.id);
  });
  const selected = selectedStoreId ? stores.find((store) => store.id === selectedStoreId) : undefined;
  if (selected && !ordered.some((store) => store.id === selected.id)) ordered.unshift(selected);
  // The Android map gives its clusterer every mapped seller.  Keeping the
  // complete current viewport here is important: a client-side display cap
  // makes cluster counts and nearby sellers disagree between Android and Web.
  return ordered;
}

export function pointIsInViewport(store: LottoStore, viewport: LottoMapViewport): boolean {
  if (store.latitude < viewport.south || store.latitude > viewport.north) return false;
  return viewport.west <= viewport.east
    ? store.longitude >= viewport.west && store.longitude <= viewport.east
    : store.longitude >= viewport.west || store.longitude <= viewport.east;
}

function normalizeSearch(value: string): string {
  return value.trim().replaceAll(/\s+/g, "").toLocaleLowerCase("ko-KR");
}
